import React, { useState } from "react";
import { GripVertical, Edit, Trash2, Eye, EyeOff, Instagram, Globe, Layout, Youtube } from "lucide-react";
import { Button } from "../../../Components/ui/shadcn/button";
import { cn } from "../../../lib/utils";
import type { RecommendedReviewListItem, RecommendedReviewPlatform } from "./recommendedReview.types";

interface DraggableReviewListProps {
    reviews: RecommendedReviewListItem[];
    onReorder: (newOrder: RecommendedReviewListItem[]) => void;
    onEdit: (review: RecommendedReviewListItem) => void;
    onDelete: (id: number) => void;
    onToggleActive: (id: number) => void;
}

const PLATFORM_LABELS: Record<RecommendedReviewPlatform, string> = {
    instagram: "INSTAGRAM",
    naver_blog: "NAVER_BLOG",
    youtube: "YOUTUBE",
    web: "WEB",
    other: "OTHER",
};

const PlatformIcon = ({ platform }: { platform: RecommendedReviewPlatform }) => {
    switch (platform) {
        case "instagram": return <Instagram size={14} className="text-pink-500" />;
        case "naver_blog": return <Layout size={14} className="text-green-500" />;
        case "youtube": return <Youtube size={14} className="text-red-500" />;
        default: return <Globe size={14} className="text-blue-500" />;
    }
};

const DraggableReviewList: React.FC<DraggableReviewListProps> = ({
    reviews,
    onReorder,
    onEdit,
    onDelete,
    onToggleActive,
}) => {
    const [draggedItemIndex, setDraggedItemIndex] = useState<number | null>(null);

    const handleDragStart = (e: React.DragEvent, index: number) => {
        setDraggedItemIndex(index);
        e.dataTransfer.effectAllowed = "move";
        const target = e.target as HTMLElement;
        target.classList.add("opacity-50");
    };

    const handleDragEnd = (e: React.DragEvent) => {
        const target = e.target as HTMLElement;
        target.classList.remove("opacity-50");
        setDraggedItemIndex(null);
    };

    const handleDragOver = (e: React.DragEvent, index: number) => {
        e.preventDefault();
        if (draggedItemIndex === null || draggedItemIndex === index) return;

        const newReviews = [...reviews];
        const draggedItem = newReviews[draggedItemIndex];
        newReviews.splice(draggedItemIndex, 1);
        newReviews.splice(index, 0, draggedItem);

        // Update display_order based on new index
        const updatedReviews = newReviews.map((item, i) => ({
            ...item,
            display_order: i,
        }));

        setDraggedItemIndex(index);
        onReorder(updatedReviews);
    };

    return (
        <div className="space-y-2">
            {reviews.length === 0 ? (
                <div className="text-center py-10 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200 text-gray-400 text-sm">
                    등록된 리뷰가 없습니다. 리뷰를 추가하여 고객들에게 보여주세요.
                </div>
            ) : (
                reviews.map((review, index) => (
                    <div
                        key={review.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragEnd={handleDragEnd}
                        onDragOver={(e) => handleDragOver(e, index)}
                        className={cn(
                            "flex items-center gap-4 bg-white p-3 rounded-lg border border-gray-200 shadow-sm transition-all hover:border-gray-300",
                            draggedItemIndex === index && "ring-2 ring-black bg-gray-50"
                        )}
                    >
                        <div className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600">
                            <GripVertical size={20} />
                        </div>

                        <div className="w-16 h-12 bg-gray-100 rounded-md overflow-hidden shrink-0 border border-gray-100 flex items-center justify-center">
                            {review.thumbnail_url ? (
                                <img src={review.thumbnail_url} alt={review.title} className="w-full h-full object-cover" />
                            ) : (
                                <div className="flex flex-col items-center justify-center text-[8px] text-gray-300">
                                    <PlatformIcon platform={review.platform} />
                                    <span>No Img</span>
                                </div>
                            )}
                        </div>

                        <div className="flex-grow min-w-0">
                            <div className="flex items-center gap-1 mb-0.5">
                                <PlatformIcon platform={review.platform} />
                                <span className="text-[10px] uppercase font-bold text-gray-400">
                                    {PLATFORM_LABELS[review.platform]}
                                </span>
                            </div>
                            <div className="text-sm font-semibold text-gray-900 truncate">{review.title || "무제 리뷰"}</div>
                            <div className="text-[10px] text-gray-500 truncate">{review.author_name || "익명"} | 순서: {review.display_order + 1}</div>
                        </div>

                        <div className="flex items-center gap-1">
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-gray-400 hover:text-gray-600"
                                onClick={() => onToggleActive(review.id)}
                            >
                                {review.is_active ? <Eye size={18} /> : <EyeOff size={18} className="text-red-400" />}
                            </Button>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-gray-400 hover:text-blue-600"
                                onClick={() => onEdit(review)}
                            >
                                <Edit size={18} />
                            </Button>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-gray-400 hover:text-red-600"
                                onClick={() => onDelete(review.id)}
                            >
                                <Trash2 size={18} />
                            </Button>
                        </div>
                    </div>
                ))
            )}
        </div>
    );
};

export default DraggableReviewList;
